import { usePermissions } from 'react-admin';
import { permissionManager } from '../../../../../permissions/manager';
import { AdminMiniAbility, AdminMiniDescriptor } from '../admin-mini.types';

export interface AdminMiniAccess {
    /** Whether the signed-in admin may do this. */
    can: (ability: AdminMiniAbility) => boolean;
    /** Still fetching the permission payload, so nothing should be drawn or refused yet. */
    loading: boolean;
}

/**
 * What the signed-in admin may do with one Mini tool.
 *
 * Three of the five abilities cost something real - running a batch sends mail to people outside
 * the company and spends credits, sending by hand does the same one message at a time, and asking
 * the assistant for a draft spends LLM budget - so each has its own ACL id and each is checked
 * here rather than assumed from the fact that the tab opened.
 *
 * **This is the belt and HQ-API is the braces.** Every route behind these buttons carries its own
 * `#[IsGranted]`, so hiding a control here changes nothing about what the server will accept. What
 * it changes is what the admin sees: an ungated button is a button that can only fail, which is
 * the reasoning already written on `NokProcessingFailuresList` for `HQ_Data::nok_rescore`.
 *
 * `loading` is returned rather than folded into `can`, because the two states need opposite
 * treatment. While permissions are in flight the honest answer is "not yet", and a screen that
 * treats that as "no" flashes a refusal at an admin who is perfectly entitled to be there.
 */
export function useAdminMiniAccess(tool: AdminMiniDescriptor): AdminMiniAccess {
    const { permissions, isPending } = usePermissions();

    return {
        can: (ability: AdminMiniAbility): boolean =>
            permissionManager.isAllowed(permissions, tool.acl[ability]),
        loading: isPending === true,
    };
}
